(function () {
  class BrickshopCollectionParts {
    constructor(root) {
      this.root = root;
      this.grid = root.querySelector('[data-brickshop-parts-grid]');
      this.pagination = root.querySelector('[data-brickshop-pagination]');
      this.cards = Array.from(root.querySelectorAll('[data-brickshop-part-card]'));
      this.searchCards = Array.from(root.querySelectorAll('[data-brickshop-part-card], [data-brickshop-preview-card]'));
      this.searchInput = root.querySelector('[data-brickshop-search]');
      this.searchStatus = root.querySelector('[data-brickshop-search-status]');
      this.searchEmpty = root.querySelector('[data-brickshop-search-empty]');
      this.modal = root.querySelector('[data-brickshop-modal]');
      this.modalMessage = root.querySelector('[data-brickshop-modal-message]');
      this.localQuantities = {};
      this.loadingVariants = new Set();
      this.cartState = { items: {}, item_count: 0, rawItems: [] };
      this.settings = this.readSettings();
      this.originalGridHtml = this.grid ? this.grid.innerHTML : '';
      this.collectionSearchIndex = null;
      this.collectionSearchPromise = null;
      this.searchRequestId = 0;
      this.isSearchActive = false;
      this.initialPartName = this.getUrlParam('partname');
      this.hasAppliedInitialPartSearch = false;
    }

    init() {
      this.bindEvents();
      this.fetchCartState();
      this.applyInitialUrlSearch();

      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.cartUpdate) {
        subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
          if (event && event.cartData) {
            this.setCartState(event.cartData);
          } else {
            this.fetchCartState();
          }
        });
      }
    }

    readSettings() {
      return {
        collectionHandle: this.root.dataset.collectionHandle || '',
        collectionUrl: this.root.dataset.collectionUrl || window.location.pathname,
        sectionId: this.root.dataset.sectionId || '',
        sectionType: this.root.dataset.sectionType || 'main-collection-product-grid',
        collectionTitle: this.root.dataset.collectionTitle || '',
        productSetName: this.root.dataset.productSetName || this.root.dataset.collectionTitle || '',
        setNumber: this.root.dataset.setNumber || '',
        addLabel: this.root.dataset.addLabel || 'Add to Request',
        addedLabel: this.root.dataset.addedLabel || 'Added',
        addingLabel: this.root.dataset.addingLabel || 'Adding...',
        unavailableLabel: this.root.dataset.unavailableLabel || 'Unavailable',
        searchLoadingMessage: this.root.dataset.searchLoadingMessage || 'Searching parts...',
        limitMessage: this.root.dataset.limitMessage || 'You can request a maximum of [limit] of this part.',
        crossSetMessage:
          this.root.dataset.crossSetMessage ||
          'You already have parts from another product set in your cart. Please complete or clear that request before requesting parts from another set.',
      };
    }

    bindEvents() {
      this.root.addEventListener('click', (event) => {
        const quantityButton = event.target.closest('[data-brickshop-quantity-action]');
        if (quantityButton && this.root.contains(quantityButton)) {
          const card = quantityButton.closest('[data-brickshop-part-card]');
          this.changeQuantity(card, quantityButton.dataset.brickshopQuantityAction);
          return;
        }

        const addButton = event.target.closest('[data-brickshop-add]');
        if (addButton && this.root.contains(addButton)) {
          this.addToRequest(addButton.closest('[data-brickshop-part-card]'));
          return;
        }

        if (event.target.closest('[data-brickshop-modal-close]')) {
          this.closeModal();
        }
      });

      this.root.addEventListener('change', (event) => {
        if (!event.target.matches('[data-brickshop-quantity-input]')) return;
        this.setQuantityFromInput(event.target.closest('[data-brickshop-part-card]'), event.target);
      });

      if (this.searchInput) {
        this.searchInput.addEventListener('input', () => this.filterCards());
      }
    }

    async fetchCartState() {
      try {
        const response = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
        const cart = await response.json();
        this.setCartState(cart);
      } catch (error) {
        console.error(error);
      }
    }

    setCartState(cart) {
      const items = {};
      const rawItems = Array.isArray(cart.items) ? cart.items : [];

      rawItems.forEach((item) => {
        const variantId = String(item.variant_id || item.id || '');
        if (!variantId) return;

        items[variantId] = {
          key: item.key,
          quantity: Number(item.quantity) || 0,
          properties: item.properties || {},
        };
      });

      this.cartState = {
        items,
        item_count: Number(cart.item_count) || rawItems.reduce((sum, item) => sum + (Number(item.quantity) || 0), 0),
        rawItems,
      };

      this.cards.forEach((card) => this.renderCard(card));
    }

    getVariantState(card) {
      return this.cartState.items[String(card.dataset.variantId)] || null;
    }

    getMaxQuantity(card) {
      const max = parseInt(card.dataset.maxQuantity, 10);
      return Number.isFinite(max) && max > 0 ? max : 1;
    }

    getCurrentQuantity(card) {
      const variantState = this.getVariantState(card);
      if (variantState) return variantState.quantity;

      const variantId = String(card.dataset.variantId);
      return this.localQuantities[variantId] || 0;
    }

    renderCard(card) {
      const variantId = String(card.dataset.variantId);
      const variantState = this.getVariantState(card);
      const quantity = this.getCurrentQuantity(card);
      const input = card.querySelector('[data-brickshop-quantity-input]');
      const addButton = card.querySelector('[data-brickshop-add]');
      const decreaseButton = card.querySelector('[data-brickshop-quantity-action="decrease"]');
      const increaseButton = card.querySelector('[data-brickshop-quantity-action="increase"]');
      const isLoading = this.loadingVariants.has(variantId);
      const isAvailable = card.dataset.available === 'true';
      const max = this.getMaxQuantity(card);

      if (input) {
        input.value = quantity;
        input.max = max;
        input.disabled = isLoading || !isAvailable;
      }

      if (decreaseButton) decreaseButton.disabled = isLoading || !isAvailable || quantity <= 0;
      if (increaseButton) increaseButton.disabled = isLoading || !isAvailable || quantity >= max;

      if (addButton) {
        addButton.classList.toggle('is-added', Boolean(variantState));
        addButton.disabled = isLoading || !isAvailable || Boolean(variantState) || quantity < 1;

        if (!isAvailable) {
          addButton.textContent = this.settings.unavailableLabel;
        } else if (isLoading) {
          addButton.textContent = this.settings.addingLabel;
        } else if (variantState) {
          addButton.textContent = this.settings.addedLabel;
        } else {
          addButton.textContent = this.settings.addLabel;
        }
      }
    }

    async changeQuantity(card, action) {
      if (!card) return;

      const current = this.getCurrentQuantity(card);
      const next = action === 'increase' ? current + 1 : current - 1;
      await this.setQuantity(card, next);
    }

    async setQuantityFromInput(card, input) {
      if (!card || !input) return;
      await this.setQuantity(card, parseInt(input.value, 10) || 0);
    }

    async setQuantity(card, requestedQuantity) {
      const max = this.getMaxQuantity(card);
      const nextQuantity = Math.max(0, requestedQuantity);
      const variantState = this.getVariantState(card);

      if (nextQuantity > max) {
        this.showCardMessage(card, this.limitMessage(max));
        this.renderCard(card);
        return;
      }

      this.showCardMessage(card, '');

      if (!variantState) {
        this.localQuantities[String(card.dataset.variantId)] = nextQuantity;
        this.renderCard(card);
        return;
      }

      await this.updateCartQuantity(card, nextQuantity);
    }

    async addToRequest(card) {
      if (!card || this.getVariantState(card)) return;

      const quantity = this.getCurrentQuantity(card);
      const max = this.getMaxQuantity(card);

      if (quantity < 1) {
        this.showCardMessage(card, this.settings.addLabel);
        return;
      }

      if (quantity > max) {
        this.showCardMessage(card, this.limitMessage(max));
        return;
      }

      if (!this.canAddCurrentCollection()) {
        this.openModal(this.crossSetMessage());
        return;
      }

      const variantId = String(card.dataset.variantId);
      this.setLoading(variantId, true);

      try {
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
          },
          body: JSON.stringify({
            id: Number(variantId),
            quantity,
            properties: this.lineItemProperties(),
          }),
        });
        const data = await response.json();

        if (!response.ok || data.status) {
          throw new Error(data.description || data.message || 'Unable to add item');
        }

        delete this.localQuantities[variantId];
        await this.fetchCartState();
        await this.refreshCartSections(true);
        this.publishCartUpdate();
      } catch (error) {
        this.showCardMessage(card, error.message || 'Unable to add item');
      } finally {
        this.setLoading(variantId, false);
      }
    }

    async updateCartQuantity(card, quantity) {
      const variantState = this.getVariantState(card);
      if (!variantState) return;

      const variantId = String(card.dataset.variantId);
      this.setLoading(variantId, true);

      try {
        const response = await fetch('/cart/change.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
          },
          body: JSON.stringify({
            id: variantState.key,
            quantity,
          }),
        });
        const cart = await response.json();

        if (!response.ok || cart.status) {
          throw new Error(cart.description || cart.message || 'Unable to update item');
        }

        this.setCartState(cart);
        await this.refreshCartSections(false);
        this.publishCartUpdate(cart, variantId);
      } catch (error) {
        this.showCardMessage(card, error.message || 'Unable to update item');
        this.renderCard(card);
      } finally {
        this.setLoading(variantId, false);
      }
    }

    setLoading(variantId, isLoading) {
      if (isLoading) {
        this.loadingVariants.add(String(variantId));
      } else {
        this.loadingVariants.delete(String(variantId));
      }

      this.cards
        .filter((card) => String(card.dataset.variantId) === String(variantId))
        .forEach((card) => this.renderCard(card));
    }

    canAddCurrentCollection() {
      const currentHandle = this.settings.collectionHandle;
      const currentName = this.settings.productSetName;

      return this.cartState.rawItems.every((item) => {
        const properties = item.properties || {};
        const itemHandle = properties._collection_handle || properties._set_handle || '';
        const itemSetName = properties._product_set_name || properties['Product Set'] || '';

        if (itemHandle && currentHandle) return itemHandle === currentHandle;
        if (itemSetName && currentName) return itemSetName === currentName;
        return false;
      });
    }

    lineItemProperties() {
      return {
        'Product Set': this.settings.productSetName,
        _collection_handle: this.settings.collectionHandle,
        _collection_title: this.settings.collectionTitle,
        _product_set_name: this.settings.productSetName,
        _set_handle: this.settings.collectionHandle,
        _set_number: this.settings.setNumber,
      };
    }

    async refreshCartSections(openDrawer) {
      try {
        /* FETCH FRESH CART DRAWER HTML */
        var sectionResponse = await fetch('/?sections=cart-drawer,cart-icon-bubble');

        var sections = await sectionResponse.json();

        /* REPLACE CART DRAWER HTML MANUALLY */
        var parser = new DOMParser();

        var drawerHTML = parser.parseFromString(
          sections['cart-drawer'],
          'text/html'
        );

        var newDrawerInner = drawerHTML.querySelector('#CartDrawer');

        var currentDrawer = document.querySelector('#CartDrawer');

        if (newDrawerInner && currentDrawer) {
          currentDrawer.innerHTML = newDrawerInner.innerHTML;
        }

        /* UPDATE CART ICON */
        
        var bubbleHTML = parser.parseFromString(
          sections['cart-icon-bubble'],
          'text/html'
        );

        var newBubble = bubbleHTML.querySelector('#shopify-section-cart-icon-bubble');

        var currentBubble = document.querySelector('#cart-icon-bubble');

        console.log(currentBubble,'parser')

        if (newBubble && currentBubble) {
          currentBubble.innerHTML = newBubble.innerHTML;
        }

        /* OPEN DRAWER */
        var cartDrawer = document.querySelector('cart-drawer');

        if (cartDrawer) {
          cartDrawer.classList.remove('is-empty');

          setTimeout(function () {
            if (typeof cartDrawer.open === 'function') {
                  if(openDrawer){
                  cartDrawer.open();
                  }
            } else {
              if(openDrawer){
                cartDrawer.setAttribute('open', '');
                cartDrawer.classList.add('animate', 'active');
              }
            }
                }, 100);
              }

              this.requestCart = [];
              this.partQty = {};

            } catch (err) {

              console.error(err);

              this.cartError = err.message || 'Something went wrong';

            } finally {

              this.addingToCart = false;

            }
          
    }

    publishCartUpdate(cart, variantId) {
      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.cartUpdate) {
        publish(PUB_SUB_EVENTS.cartUpdate, {
          source: 'brickshop-collection-parts',
          cartData: cart || {
            items: this.cartState.rawItems,
            item_count: this.cartState.item_count,
          },
          variantId,
        });
      }
    }

    async filterCards() {
      const query = this.normalizeSearchText(this.searchInput ? this.searchInput.value : '');
      const requestId = (this.searchRequestId += 1);

      if (!query) {
        this.setSearchLoading(false);
        this.restorePaginatedGrid();
        return;
      }

      this.isSearchActive = true;
      this.togglePagination(false);
      const searchIndexPromise = this.getCollectionSearchIndex();
      this.setSearchLoading(true, requestId);

      const searchIndex = await searchIndexPromise;
      if (requestId !== this.searchRequestId) return;

      const matches = searchIndex.filter((item) => item.searchText.includes(query));
      this.renderSearchResults(matches);
      this.setSearchLoading(false, requestId);
    }

    async getCollectionSearchIndex() {
      if (this.collectionSearchIndex) return this.collectionSearchIndex;
      if (this.collectionSearchPromise) return this.collectionSearchPromise;

      this.collectionSearchPromise = this.buildCollectionSearchIndex()
        .then((index) => {
          this.collectionSearchIndex = index;
          return index;
        })
        .catch((error) => {
          console.error(error);
          const fallbackIndex = this.getFallbackSearchItems();
          this.collectionSearchIndex = this.dedupeSearchIndex(fallbackIndex);
          return this.collectionSearchIndex;
        })
        .finally(() => {
          this.collectionSearchPromise = null;
        });

      return this.collectionSearchPromise;
    }

    async buildCollectionSearchIndex() {
      const currentPage = this.getCurrentPage();
      const totalPages = this.getTotalPages();
      const pageItems = this.getGridItems(this.root).map((item) => this.serializeSearchItem(item)).filter(Boolean);

      if (!this.grid || !this.settings.sectionId || totalPages <= 1) {
        return this.dedupeSearchIndex(pageItems);
      }

      const pages = Array.from({ length: totalPages }, (_, index) => index + 1).filter((page) => page !== currentPage);
      const pageIndexes = await this.fetchSearchPages(pages);

      return this.dedupeSearchIndex(pageItems.concat(pageIndexes.flat()));
    }

    async fetchSearchPages(pages) {
      const pageIndexes = [];
      const batchSize = 4;

      for (let index = 0; index < pages.length; index += batchSize) {
        const batch = pages.slice(index, index + batchSize);
        const batchResults = await Promise.all(
          batch.map((page) =>
            this.fetchSearchPage(page).catch((error) => {
              console.error(error);
              return [];
            })
          )
        );
        pageIndexes.push(...batchResults);
      }

      return pageIndexes;
    }

    async fetchSearchPage(page) {
      let primaryItems = [];

      try {
        primaryItems = await this.fetchSearchPageBySection(page, this.settings.sectionId);
      } catch (error) {
        if (!this.settings.sectionType || this.settings.sectionType === this.settings.sectionId) {
          throw error;
        }
      }

      if (primaryItems.length || !this.settings.sectionType || this.settings.sectionType === this.settings.sectionId) {
        return primaryItems;
      }

      return this.fetchSearchPageBySection(page, this.settings.sectionType);
    }

    async fetchSearchPageBySection(page, sectionId) {
      if (!sectionId) return [];

      const url = new URL(this.settings.collectionUrl || window.location.pathname, window.location.origin);
      url.searchParams.set('section_id', sectionId);
      url.searchParams.set('page', String(page));

      const response = await fetch(url.toString(), {
        headers: {
          Accept: 'text/html',
          'X-Requested-With': 'XMLHttpRequest',
        },
      });

      if (!response.ok) {
        throw new Error(`Unable to load collection page ${page}`);
      }

      const html = await response.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      return this.getGridItems(doc).map((item) => this.serializeSearchItem(item)).filter(Boolean);
    }

    getGridItems(scope) {
      const grid = scope.querySelector('[data-brickshop-parts-grid]');
      if (!grid) return [];
      return Array.from(grid.querySelectorAll('.brickshop-collection-parts__grid-item'));
    }

    getFallbackSearchItems() {
      const currentItems = this.getGridItems(this.root).map((item) => this.serializeSearchItem(item)).filter(Boolean);
      if (currentItems.length || !this.originalGridHtml) return currentItems;

      const template = document.createElement('template');
      template.innerHTML = this.originalGridHtml;
      return Array.from(template.content.querySelectorAll('.brickshop-collection-parts__grid-item'))
        .map((item) => this.serializeSearchItem(item))
        .filter(Boolean);
    }

    serializeSearchItem(item) {
      const card = item.querySelector('[data-brickshop-part-card], [data-brickshop-preview-card]');
      if (!card) return null;

      const variantId = card.dataset.variantId || '';
      const productId = card.dataset.productId || '';
      const partNumber = card.dataset.partNumber || '';
      const searchText = this.normalizeSearchText(
        [card.dataset.searchText, partNumber, card.textContent].filter(Boolean).join(' ')
      );

      return {
        key: variantId || productId || partNumber || searchText,
        html: item.outerHTML,
        searchText,
      };
    }

    dedupeSearchIndex(items) {
      const seen = new Set();

      return items.filter((item) => {
        if (!item || !item.key || seen.has(item.key)) return false;
        seen.add(item.key);
        return true;
      });
    }

    renderSearchResults(matches) {
      if (!this.grid) {
        this.filterRenderedCards(matches);
        return;
      }

      this.grid.innerHTML = matches.map((item) => item.html).join('');
      this.refreshCardReferences();
      this.cards.forEach((card) => this.renderCard(card));

      if (this.searchEmpty) {
        this.searchEmpty.hidden = matches.length > 0;
      }
    }

    setSearchLoading(isLoading, requestId) {
      if (requestId && requestId !== this.searchRequestId) return;

      this.root.classList.toggle('is-searching', isLoading);

      if (this.searchInput) {
        this.searchInput.setAttribute('aria-busy', isLoading ? 'true' : 'false');
      }

      if (this.grid) {
        this.grid.setAttribute('aria-busy', isLoading ? 'true' : 'false');
        if (isLoading) {
          this.grid.innerHTML = this.renderSearchSkeletons();
        }
      }

      if (this.searchStatus) {
        this.searchStatus.hidden = !isLoading;
        this.searchStatus.textContent = isLoading ? this.settings.searchLoadingMessage : '';
      }

      if (this.searchEmpty && isLoading) {
        this.searchEmpty.hidden = true;
      }
    }

    renderSearchSkeletons() {
      const count = this.getSkeletonCount();
      return Array.from({ length: count }, () => this.renderSearchSkeleton()).join('');
    }

    renderSearchSkeleton() {
      return `
        <li class="brickshop-collection-parts__grid-item brickshop-collection-parts__skeleton-item" aria-hidden="true">
          <article class="brickshop-part-card brickshop-part-card--skeleton">
            <div class="brickshop-part-card__inner">
              <div class="brickshop-part-card__media brickshop-part-card__skeleton-media"></div>
              <div class="brickshop-part-card__skeleton-line brickshop-part-card__skeleton-line--title"></div>
              <div class="brickshop-part-card__controls">
                <div class="brickshop-part-card__skeleton-control"></div>
                <div class="brickshop-part-card__skeleton-button"></div>
              </div>
              <div class="brickshop-part-card__skeleton-message"></div>
            </div>
          </article>
        </li>
      `;
    }

    getSkeletonCount() {
      const currentCount = this.getGridItems(this.root).length;
      if (currentCount > 0) return Math.min(Math.max(currentCount, 6), 24);

      const desktopColumns = parseInt(
        window.getComputedStyle(this.root).getPropertyValue('--brickshop-parts-columns-desktop'),
        10
      );
      return Number.isFinite(desktopColumns) && desktopColumns > 0 ? desktopColumns * 2 : 12;
    }

    filterRenderedCards(matches) {
      const matchKeys = new Set(matches.map((item) => item.key));
      let visibleCount = 0;

      this.searchCards.forEach((card) => {
        const key = card.dataset.variantId || card.dataset.productId || card.dataset.partNumber || '';
        const isVisible = matchKeys.has(key);
        const gridItem = card.closest('.brickshop-collection-parts__grid-item');

        if (gridItem) gridItem.hidden = !isVisible;
        card.hidden = !isVisible;
        if (isVisible) visibleCount += 1;
      });

      if (this.searchEmpty) {
        this.searchEmpty.hidden = visibleCount > 0;
      }
    }

    restorePaginatedGrid() {
      this.isSearchActive = false;
      this.setSearchLoading(false);
      this.togglePagination(true);

      if (this.grid && this.originalGridHtml) {
        this.grid.innerHTML = this.originalGridHtml;
      } else {
        this.searchCards.forEach((card) => {
          const gridItem = card.closest('.brickshop-collection-parts__grid-item');
          if (gridItem) gridItem.hidden = false;
          card.hidden = false;
        });
      }

      if (this.searchEmpty) {
        this.searchEmpty.hidden = true;
      }

      this.refreshCardReferences();
      this.cards.forEach((card) => this.renderCard(card));
    }

    refreshCardReferences() {
      this.cards = Array.from(this.root.querySelectorAll('[data-brickshop-part-card]'));
      this.searchCards = Array.from(
        this.root.querySelectorAll('[data-brickshop-part-card], [data-brickshop-preview-card]')
      );
    }

    togglePagination(isVisible) {
      if (this.pagination) {
        this.pagination.hidden = !isVisible;
      }
    }

    getCurrentPage() {
      const page = parseInt(this.grid ? this.grid.dataset.currentPage : '1', 10);
      return Number.isFinite(page) && page > 0 ? page : 1;
    }

    getTotalPages() {
      const pages = parseInt(this.grid ? this.grid.dataset.totalPages : '1', 10);
      return Number.isFinite(pages) && pages > 0 ? pages : 1;
    }

    normalizeSearchText(value) {
      return String(value || '').trim().replace(/\s+/g, ' ').toLowerCase();
    }

    getUrlParam(name) {
      try {
        const params = new URLSearchParams(window.location.search);
        return (params.get(name) || '').trim();
      } catch (error) {
        return '';
      }
    }

    async applyInitialUrlSearch() {
      if (this.hasAppliedInitialPartSearch) return;
      this.hasAppliedInitialPartSearch = true;

      const partName = this.initialPartName;
      if (!partName || !this.searchInput) return;

      this.searchInput.value = partName;

      // Let the page render, then run the existing search flow.
      await this.filterCards();
    }

    limitMessage(limit) {
      return this.settings.limitMessage
        .replace(/\{\{\s*limit\s*\}\}/g, limit)
        .replace(/\[limit\]/g, limit)
        .replace(/\[quantity\]/g, limit);
    }

    crossSetMessage() {
      const otherItem = this.cartState.rawItems.find((item) => {
        const properties = item.properties || {};
        const handle = properties._collection_handle || properties._set_handle || '';
        return handle && handle !== this.settings.collectionHandle;
      });

      if (otherItem) {
        const properties = otherItem.properties || {};
        const setName = properties._product_set_name || properties['Product Set'] || properties._collection_title;

        if (setName) {
          return `You already have parts from the ${setName} set in your cart. Please complete or clear that request before requesting parts from another set.`;
        }
      }

      return this.settings.crossSetMessage;
    }

    showCardMessage(card, message) {
      const messageElement = card ? card.querySelector('[data-brickshop-card-message]') : null;
      if (messageElement) messageElement.textContent = message;
    }

    openModal(message) {
      if (!this.modal) return;
      if (this.modalMessage) this.modalMessage.textContent = message;
      this.modal.hidden = false;
      const closeButton = this.modal.querySelector('[data-brickshop-modal-close]');
      if (closeButton) closeButton.focus();
    }

    closeModal() {
      if (this.modal) this.modal.hidden = true;
    }
  }

  function initRoot(root) {
    if (!root || root.dataset.brickshopCollectionPartsReady === 'true') return;
    root.dataset.brickshopCollectionPartsReady = 'true';
    const controller = new BrickshopCollectionParts(root);
    root.brickshopCollectionParts = controller;
    controller.init();
  }

  window.brickshopCollectionParts = function () {
    return {
      init() {
        initRoot(this.$el);
      },
    };
  };

  document.addEventListener('alpine:init', function () {
    if (window.Alpine) {
      window.Alpine.data('brickshopCollectionParts', window.brickshopCollectionParts);
    }
  });

  document.addEventListener('DOMContentLoaded', function () {
    document.querySelectorAll('[data-brickshop-collection-parts]').forEach(initRoot);
  });
})();
